import { Lock, Server, Radar, SlidersHorizontal } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { XMark } from "./Logo";

const BLOCKS = [
    {
        t: "Account Protection",
        d: "Multiple layers of account protection, including sign-in and device controls.",
        icon: Lock,
        tid: "security-block-account",
    },
    {
        t: "Secure Architecture",
        d: "Security-first infrastructure designed with isolation and resilience in mind.",
        icon: Server,
        tid: "security-block-architecture",
    },
    {
        t: "Risk Monitoring",
        d: "Continuous platform monitoring designed to flag unusual activity.",
        icon: Radar,
        tid: "security-block-risk",
    },
    {
        t: "User Controls",
        d: "Granular controls that keep users in charge of their own account.",
        icon: SlidersHorizontal,
        tid: "security-block-controls",
    },
];

const ShieldVisual = () => (
    <div
        data-testid="security-shield-visual"
        className="relative mx-auto flex h-72 w-72 items-center justify-center sm:h-80 sm:w-80 lg:h-[22rem] lg:w-[22rem]"
    >
        <div className="absolute inset-[6%] rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.14),transparent_70%)]" />
        <div className="absolute inset-0 animate-spin-slow rounded-full border border-dashed border-cyan-400/15" />
        <div className="absolute inset-[14%] rounded-full border border-white/[0.05]" />

        <svg viewBox="0 0 200 200" className="relative h-[62%] w-[62%]" aria-hidden="true">
            <defs>
                <linearGradient id="shield-g" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#00D1FF" />
                    <stop offset="60%" stopColor="#168BFF" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                </linearGradient>
                <clipPath id="shield-clip">
                    <path d="M100 18 L162 44 V100 C162 140 132 166 100 182 C68 166 38 140 38 100 V44 Z" />
                </clipPath>
            </defs>
            <path
                d="M100 18 L162 44 V100 C162 140 132 166 100 182 C68 166 38 140 38 100 V44 Z"
                fill="rgba(11,16,32,0.85)"
                stroke="url(#shield-g)"
                strokeWidth="2"
            />
            <path
                d="M100 34 L148 54 V100 C148 130 126 152 100 166 C74 152 52 130 52 100 V54 Z"
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
            />
            <g clipPath="url(#shield-clip)">
                <rect x="30" y="98" width="140" height="2.5" fill="url(#shield-g)" opacity="0.7" className="animate-scan-y" />
            </g>
        </svg>

        <div className="absolute flex h-[26%] w-[26%] items-center justify-center">
            <XMark className="h-full w-full" strokeWidth={2.2} />
        </div>
    </div>
);

export default function Security() {
    return (
        <section id="security" data-testid="security-section" className="relative overflow-hidden bg-void py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="grid items-center gap-16 lg:grid-cols-2">
                    <Reveal>
                        <ShieldVisual />
                    </Reveal>

                    <div>
                        <Reveal>
                            <Eyebrow>Security</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                                Security is <span className="text-grad-blue">built in.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                                Designed around a security-first mindset across the platform —
                                from account sign-in to infrastructure architecture.
                            </p>
                        </Reveal>

                        <div className="mt-10 grid gap-3 sm:grid-cols-2">
                            {BLOCKS.map((b, i) => (
                                <Reveal key={b.t} delay={0.1 + i * 0.07}>
                                    <div
                                        data-testid={b.tid}
                                        className="group h-full rounded-lg border border-white/[0.07] bg-panel/40 p-6 transition-all duration-500 hover:border-cyber/40 hover:bg-panel/70"
                                    >
                                        <b.icon className="h-5 w-5 text-cyber" strokeWidth={1.5} />
                                        <h3 className="mt-4 font-grotesk text-sm font-semibold uppercase tracking-[0.14em] text-white">
                                            {b.t}
                                        </h3>
                                        <p className="mt-2 text-[13px] leading-relaxed text-slate-400">
                                            {b.d}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.3}>
                            <p className="mt-8 text-[11px] leading-relaxed text-slate-600">
                                No platform can promise absolute security. TAVONX is engineered
                                with a security-first approach — protections described are
                                design goals, not guarantees.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
